import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
type LegalModalProps = {
  document: LegalDocument | null;
  onClose: () => void;
};
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    closeRef.current?.focus();
    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-93" data-mp="1cuk5gte71oya">
      {doc && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-94" data-mp="1ctiueox6h2gk">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-0 bg-ink/50" onClick={onClose} data-id="element-95" data-mp="1frcm66ndi010" />

          <motion.div initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-h-[85vh] sm:rounded-2xl" data-id="element-96" data-mp="ry466emkfy89">
            <div className="flex items-start justify-between gap-6 border-b border-line px-6 py-5 sm:px-9 sm:py-6" data-id="element-97" data-mp="e8aiw0qplm0n">
              <div data-id="element-98" data-mp="672sq2txf1jn">
                <h2 id="legal-modal-title" className="font-display text-2xl font-normal text-ink sm:text-3xl" data-id="element-99" data-mp="66jlbg1wjvjv">
                  {doc.title}
                </h2>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted" data-id="element-100" data-mp="fww4p4xzdiof">
                  {doc.updated}
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="-mr-1 rounded-full p-2 text-muted transition-colors duration-150 ease-out hover:bg-cream hover:text-ink" data-id="element-101" data-mp="3jduvryzua7ce">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-102" data-mp="3od7qf5wycipj" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-7 sm:px-9 sm:py-8" data-id="element-103" data-mp="18ljvfg1i109d">
              <p className="text-[15px] leading-relaxed text-muted" data-id="element-104" data-mp="13nid4xbwd4yy">{doc.intro}</p>

              <div className="mt-8 space-y-7" data-id="element-105" data-mp="13pkyv560obxv">
                {doc.sections.map(section => <section key={section.heading} data-id="element-106" data-mp="1b941qvys0zxd">
                    <h3 className="text-sm font-semibold text-ink" data-id="element-107" data-mp="1g6vwdxqt3059">{section.heading}</h3>
                    <div className="mt-2 space-y-3" data-id="element-108" data-mp="126upo1nv0yhv">
                      {section.body.map(paragraph => <p key={paragraph} className="text-[14px] leading-relaxed text-muted" data-id="element-109" data-mp="168g07gcx90ow">
                          {paragraph}
                        </p>)}
                    </div>
                  </section>)}
              </div>
            </div>

            <div className="border-t border-line bg-cream px-6 py-4 sm:px-9" data-id="element-110" data-mp="1l3w1o6ftzyvb">
              <button type="button" onClick={onClose} className="text-sm font-semibold text-sage-dark transition-colors duration-150 ease-out hover:text-ink" data-id="element-111" data-mp="1p60p2nforz9i">
                Entendido, cerrar
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}